import type { Feature, FeatureCollection, Polygon } from 'geojson'

import type { Activity, MapBounds } from '@/features/discovery/types'

/**
 * Viewer statuses that unlock the exact location and the directions action.
 * Everyone else gets a deterministic privacy area from the API and a matching
 * 300 m radius on the map (D-085).
 */
export const EXACT_LOCATION_STATUSES = new Set(['creator', 'joined', 'attended'])

/** Privacy area radius shown for approximate pins (D-085). */
export const APPROXIMATE_RADIUS_M = 300

/**
 * The API filters by the coarse (2-decimal) point while the map draws the
 * privacy area centre. Expand the requested bounds by roughly the worst-case
 * coarse rounding plus the area-centre jitter (~1.1 km) so zooming tightly on
 * a pin does not drop its activity from the result set (D-087/D-088).
 */
export const MAP_BOUNDS_PADDING_DEG = 0.01

export function padMapBounds(bounds: MapBounds): MapBounds {
  return {
    south: Math.max(-90, bounds.south - MAP_BOUNDS_PADDING_DEG),
    north: Math.min(90, bounds.north + MAP_BOUNDS_PADDING_DEG),
    west: Math.max(-180, bounds.west - MAP_BOUNDS_PADDING_DEG),
    east: Math.min(180, bounds.east + MAP_BOUNDS_PADDING_DEG),
  }
}

export function hasExactLocation(activity: Activity): boolean {
  return EXACT_LOCATION_STATUSES.has(activity.viewerStatus ?? '')
}

export function isApproximateLocation(activity: Activity): boolean {
  return !hasExactLocation(activity)
}

/**
 * Coordinates used by the map marker. The API point is the centre of the
 * privacy area for approximate viewers, so the pin sits exactly in the middle
 * of the circle drawn by `buildAreas` (D-094).
 */
export function markerCoordinates(activity: Activity): {
  latitude: number
  longitude: number
} | null {
  if (activity.latitude === null || activity.longitude === null) return null
  if (
    !Number.isFinite(activity.latitude) ||
    !Number.isFinite(activity.longitude)
  ) {
    return null
  }
  return {
    latitude: activity.latitude,
    longitude: activity.longitude,
  }
}

/**
 * Circle polygon around a point. The pin uses the same API point, so it sits
 * exactly at the circle centre (D-094).
 */
export function circlePolygon(
  latitude: number,
  longitude: number,
  radiusMetres: number,
  color: string,
): Feature<Polygon, { color: string }> {
  const points: Array<[number, number]> = []
  const steps = 48
  const cosine = Math.max(Math.cos((latitude * Math.PI) / 180), 0.01)
  for (let step = 0; step < steps; step += 1) {
    const angle = (step / steps) * Math.PI * 2
    const deltaLat = (radiusMetres * Math.cos(angle)) / 111320
    const deltaLon = (radiusMetres * Math.sin(angle)) / (111320 * cosine)
    points.push([longitude + deltaLon, latitude + deltaLat])
  }
  points.push(points[0])
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [points] },
    properties: { color },
  }
}

/**
 * GeoJSON areas for the map source. Only approximate locations get a circle;
 * creator and participants see the exact pin without a privacy radius.
 */
export function buildAreas(
  activities: Activity[],
): FeatureCollection<Polygon, { color: string }> {
  const features: Array<Feature<Polygon, { color: string }>> = []
  for (const activity of activities) {
    if (activity.latitude === null || activity.longitude === null) continue
    if (
      !Number.isFinite(activity.latitude) ||
      !Number.isFinite(activity.longitude)
    ) {
      continue
    }
    if (!isApproximateLocation(activity)) continue
    features.push(
      circlePolygon(
        activity.latitude,
        activity.longitude,
        APPROXIMATE_RADIUS_M,
        activity.categoryColor || '#00645f',
      ),
    )
  }
  return { type: 'FeatureCollection', features }
}
